import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LayoutModeService } from '../../../../core/services/layout-mode.service';

import { faEllipsisV } from '@awesome.me/kit-bde9a98016/icons/sharp/solid';

import { AppCoreService } from '../../../../core/services/app-core.service';
import { HelpService } from '../../../../core/services/help.service';

import { AhsTranslationService } from '@nx-ahs/base-translate';

import { TutorialConfig } from '../../../../shared/models/portal/tutorialConfig';
import { LanguageService } from '../../../../core/services/language.service';
import { SecurityService } from '../../../../core/services/security.service';
import { UserModel } from '../../../../shared/models/user/user';

@Component({
	selector: 'ahs-tutorial',
	templateUrl: './tutorial.component.html',
	styleUrls: ['./tutorial.component.scss'],
	standalone: false
})
export class TutorialComponent implements OnInit {
	public faEllipsisV = faEllipsisV;
	public showBellNotification: boolean;
	public showAccountActionsMenu: boolean;
	public showAccountSelector: boolean;
	public showTutorial = false;
	public tutorialConfig: TutorialConfig;
	public user: UserModel;
	public fontSize: string;
	public excludeTutorialOnRoutes: string[] = [];
	public isMobileLayout = false;
	public mobilePhase: 'items' | 'header' = 'items';

	@HostListener('document:keydown.escape')
	fn() {
		this.handleOverlayAction();
	}

	constructor(
		public ahsTranslationService: AhsTranslationService,
		public router: Router,
		private languageService: LanguageService,
		private appCoreService: AppCoreService,
		private helpService: HelpService,
		private securityService: SecurityService,
		private layoutModeService: LayoutModeService
	) {
		this.layoutModeService.isDesktopLayout$.subscribe((isDesktop) => {
			this.isMobileLayout = !isDesktop;
		});
		this.helpService.showTutorial.subscribe((val) => {
			if (val) {
				this.user = this.securityService.getCurrentUser();
				this.mobilePhase = 'items';
			}
			const isMobile = !this.layoutModeService.isDesktopLayout;
			if (this.appCoreService.clientConfig?.helpMenu?.hideTutorialOnDesktop == false || isMobile)
				this.showTutorial = val;
		});
		this.ahsTranslationService.onLangChange.subscribe((data) => this.getLanguage());
	}

	async ngOnInit() {
		await this.getLanguage();
		this.fontSize = this.appCoreService.clientConfig?.helpMenu?.tutorialFontSize;
		if (!this.fontSize) this.fontSize = '1em';

		if (this.appCoreService.clientConfig?.helpMenu?.excludeTutorialOnRoutes) {
			this.excludeTutorialOnRoutes = this.appCoreService.clientConfig.helpMenu.excludeTutorialOnRoutes;
		}
	}

	public isForbiddenRoute(): boolean {
		return this.excludeTutorialOnRoutes.includes(this.router.url);
	}

	public handleOverlayAction(): void {
		if (this.isMobileLayout && this.mobilePhase === 'items') {
			this.mobilePhase = 'header';
		} else {
			this.helpService.showTutorial.next(false);
		}
	}

	public hideTutorial(): void {
		this.helpService.showTutorial.next(false);
	}

	public getArrowStyle(menuItemIndex: number): any {
		const position = this.helpService.getMenuItemPositionBySortedIndex(menuItemIndex);
		const thisItem =
			this.appCoreService.clientConfig?.languages[0]?.pages?.tutorial?.data?.tutorialItems[menuItemIndex];
		if (position) {
			// Calculate the vertical center of the ion-item
			const menuCenterY = position.top + position.height / 2;
			// Position the div so the SVG's arrow endpoint (10,70) aligns with the menu item's center
			const divTop = menuCenterY - 70; // 70px is the y-coordinate of the arrow's end in the SVG

			return {
				left: thisItem.POSITION__LEFT_ALIGN, // This is not dynamic now, but if you future person wants to make this dynamic, go for it
				top: `${divTop}px`,
				'text-align': 'center',
				position: 'absolute',
				overflow: 'visible'
			};
		}
		return {
			left: '65px', // Default position, these values can be null on app startup, so here are some defaults. The values are calculated when the tutorial is shown, so the expectation is to never see the arrows with these values.
			top: '0px',
			'text-align': 'center',
			position: 'absolute',
			overflow: 'visible'
		};
	}

	public getMobileArrowStyle(menuItemIndex: number): any {
		const thisItem =
			this.appCoreService.clientConfig.languages[0]?.pages?.tutorial?.data?.tutorialMobileItems[menuItemIndex];
		return {
			width: thisItem.POSITION__WIDTH_ALIGN,
			bottom: thisItem.POSITION_BOTTOM_ALIGN,
			'padding-left': thisItem.POSITION_PADDING_LEFT,
			'text-align': 'center',
			position: 'absolute',
			overflow: 'visible'
		};
	}

	private async getLanguage(): Promise<void> {
		// get translations from client config
		const configLanguages = [];
		this.appCoreService.clientConfig.languages?.forEach((l) => {
			configLanguages.push(l.pages.tutorial);
		});
		const languages = await this.languageService.getLanguagesForPublicPage('tutorial').toPromise();
		this.ahsTranslationService.loadTranslations(...languages.locales);

		this.loadAppContentHeaderItems();
		this.loadTutorialItems();
	}

	private loadAppContentHeaderItems(): void {
		// get bool to determine if to show the below from client config
		this.showBellNotification = this.appCoreService.clientConfig.appContentHeader?.showBellNotification;
		this.showAccountSelector = this.appCoreService.clientConfig.accountSelectorConfig?.showAccountSelector;
		this.showAccountActionsMenu = this.appCoreService.clientConfig.appContentHeader?.showAccountActionsMenu;
	}

	private loadTutorialItems(): void {
		const currentLangCode = this.ahsTranslationService.currentLanguage.code;

		//get the tutorial items based on the langauge
		// note the positions in the cleint config need to be manually changed and tested based on menu item names in all languages
		// TODO - Move the config object outside of the language array now that the translations are handled in the DB
		for (let i = 0; i < this.appCoreService.clientConfig.languages?.length; i++) {
			if (this.appCoreService.clientConfig.languages[i].code === 'en') {
				// for (var x = 0; x < this.appCoreService.clientConfig.languages[i].pages.tutorial.data.tutorialItems.length; x++) {
				//     this.tutorialItems.push(this.appCoreService.clientConfig.languages[i].pages.tutorial.data.tutorialItems[x]);
				// }
				this.tutorialConfig = this.appCoreService.clientConfig.languages[i].pages.tutorial.data;
			}
		}
	}
}
